import { useEffect, useMemo, useState } from 'react';
import { Beaker, Blocks, Sparkles } from 'lucide-react';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { AssumptionPanel } from '@/components/calculator/AssumptionPanel';
import { NumberField } from '@/components/calculator/NumberField';
import { ContributionTimingControls, type ContributionTiming } from '@/components/calculator/ContributionTimingControls';
import { ResultSummaryCard } from '@/components/calculator/ResultSummaryCard';
import { ChartCard } from '@/components/calculator/ChartCard';
import { ShareExportRow } from '@/components/calculator/ShareExportRow';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/toast';

type Props = {
  route: RouteDefinition;
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
}

export function RoutePlaceholder({ route }: Props) {
  const { toast } = useToast();

  const [currentAge, setCurrentAge] = useState(32);
  const [targetAge, setTargetAge] = useState(57);
  const [annualSpend, setAnnualSpend] = useState(64000);
  const [contributionAmount, setContributionAmount] = useState(2200);
  const [contributionMode, setContributionMode] = useState<ContributionTiming>('monthly');
  const [growthRate, setGrowthRate] = useState(7);
  const [inflationAdjusted, setInflationAdjusted] = useState(true);
  const [showRealDollars, setShowRealDollars] = useState(false);
  const [scenario, setScenario] = useState('balanced');

  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  const projection = useMemo(() => {
    const yearsRemaining = Math.max(targetAge - currentAge, 1);
    const yearlyContribution = contributionMode === 'monthly' ? contributionAmount * 12 : contributionAmount;

    const scenarioMultiplier =
      scenario === 'conservative' ? 0.88 : scenario === 'aggressive' ? 1.2 : 1;

    const adjustedGrowthRate = (growthRate / 100) * scenarioMultiplier;
    const contributionGrowthRate = inflationAdjusted ? 0.025 : 0;
    const growthMultiplier = Math.pow(1 + adjustedGrowthRate, yearsRemaining);
    const contributionGrowthMultiplier = Math.pow(1 + contributionGrowthRate, yearsRemaining);

    const nominalValue =
      adjustedGrowthRate === contributionGrowthRate
        ? yearlyContribution * yearsRemaining * Math.pow(1 + adjustedGrowthRate, yearsRemaining - 1)
        : yearlyContribution * ((growthMultiplier - contributionGrowthMultiplier) / (adjustedGrowthRate - contributionGrowthRate));

    const inflationDiscount = showRealDollars ? Math.pow(1.025, yearsRemaining) : 1;
    const projectedPortfolio = nominalValue / inflationDiscount;

    const targetNumber = annualSpend * 25;
    const readinessRatio = projectedPortfolio / targetNumber;
    const gap = targetNumber - projectedPortfolio;

    return {
      yearsRemaining,
      projectedPortfolio,
      targetNumber,
      readinessRatio,
      gap,
      yearlyContribution,
      coastAge: Math.min(targetAge, Math.max(currentAge + Math.round(yearsRemaining * 0.55), currentAge + 1))
    };
  }, [annualSpend, contributionAmount, contributionMode, currentAge, growthRate, inflationAdjusted, scenario, showRealDollars, targetAge]);

  const sparklineBars = useMemo(() => {
    return Array.from({ length: 8 }, (_, index) => {
      const progress = (index + 1) / 8;
      const value = projection.projectedPortfolio * progress;
      return {
        yearLabel: currentAge + Math.round(projection.yearsRemaining * progress),
        value,
        barHeight: Math.max(14, Math.round(progress * 132))
      };
    });
  }, [currentAge, projection.projectedPortfolio, projection.yearsRemaining]);

  const rows = useMemo(
    () =>
      sparklineBars.map((bar) => ({
        year: bar.yearLabel,
        portfolio: bar.value,
        withdrawal: bar.value * 0.04,
        fundedPercent: (bar.value / projection.targetNumber) * 100
      })),
    [projection.targetNumber, sparklineBars]
  );

  const copyShareLink = async () => {
    const shareUrl = new URL(route.canonicalPath, window.location.origin).toString();

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: 'Share URL copied', description: shareUrl, variant: 'success' });
    } catch {
      toast({
        title: 'Clipboard unavailable',
        description: `Copy manually: ${shareUrl}`,
        variant: 'warning'
      });
    }
  };

  return (
    <AppLayout>
      <section className="hero-shell overflow-hidden rounded-2xl border border-border p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
          <span className="chip">
            <Blocks className="h-3.5 w-3.5" />
            Preview Mode
          </span>
          <span className="chip">
            <Sparkles className="h-3.5 w-3.5" />
            {route.isLegacyAlias ? 'Legacy Alias Route' : 'Canonical Route'}
          </span>
          <span className="chip">
            <Beaker className="h-3.5 w-3.5" />
            Scenario Harness
          </span>
        </div>

        <h1 className="mt-4 max-w-3xl font-display text-4xl leading-tight text-foreground md:text-5xl">{route.title}</h1>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground md:text-base">{route.description}</p>
        <p className="mt-2 text-xs text-muted-foreground">Canonical route: {route.canonicalPath}</p>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <AssumptionPanel
          title="Calculator Inputs (shared primitives)"
          description="This preview uses reusable calculator form blocks and synthetic scenario output."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <NumberField id="currentAge" label="Current age" value={currentAge} min={18} max={95} onChange={setCurrentAge} />
            <NumberField id="targetAge" label="Target retirement age" value={targetAge} min={25} max={95} onChange={setTargetAge} />
            <NumberField
              id="annualSpend"
              label="Target annual spend"
              value={annualSpend}
              min={12000}
              step={500}
              prefix="$"
              onChange={setAnnualSpend}
            />
            <NumberField
              id="contributionAmount"
              label={contributionMode === 'monthly' ? 'Monthly contribution' : 'Annual contribution'}
              value={contributionAmount}
              min={0}
              step={100}
              prefix="$"
              helperText="Used to generate synthetic preview output for this route shell."
              onChange={setContributionAmount}
            />
          </div>

          <ContributionTimingControls
            value={contributionMode}
            onValueChange={setContributionMode}
            inflationAdjusted={inflationAdjusted}
            onInflationAdjustedChange={setInflationAdjusted}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-2 text-sm" htmlFor="route-placeholder-scenario">
              <span className="text-sm font-semibold text-foreground">Scenario profile</span>
              <Select
                id="route-placeholder-scenario"
                value={scenario}
                onChange={(event) => setScenario(event.target.value)}
                options={[
                  { value: 'conservative', label: 'Conservative' },
                  { value: 'balanced', label: 'Balanced' },
                  { value: 'aggressive', label: 'Aggressive' }
                ]}
              />
            </label>

            <div className="space-y-2 rounded-md border border-border bg-secondary/35 p-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-foreground">Show real dollars</span>
                <Switch checked={showRealDollars} onCheckedChange={setShowRealDollars} ariaLabel="Toggle inflation-adjusted display" />
              </div>
              <p className="text-xs text-muted-foreground">When enabled, nominal projections are discounted by a 2.5% inflation assumption.</p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold text-foreground">Expected annual return</p>
            <Slider value={growthRate} min={1} max={12} step={0.5} onValueChange={setGrowthRate} />
          </div>
        </AssumptionPanel>

        <div className="space-y-4">
          <ResultSummaryCard
            label="Projected Portfolio"
            value={formatCurrency(projection.projectedPortfolio)}
            note={`at age ${targetAge}`}
            trend={projection.readinessRatio >= 1 ? 'up' : 'flat'}
          />
          <ResultSummaryCard
            label="Target Number"
            value={formatCurrency(projection.targetNumber)}
            note="uses 25x annual spend"
            trend="flat"
          />
          <ResultSummaryCard
            label="Funding Gap"
            value={formatCurrency(Math.max(projection.gap, 0))}
            note={projection.gap <= 0 ? 'Target reached in this scenario' : 'remaining to full FIRE target'}
            trend={projection.gap <= 0 ? 'up' : 'down'}
          />

          <ShareExportRow
            onCopyShare={copyShareLink}
            onExportCsv={() => toast({ title: 'CSV export unavailable', description: 'CSV export is not configured for this preview route.' })}
            onExportPdf={() => toast({ title: 'PDF export unavailable', description: 'PDF export is not configured for this preview route.' })}
          />

          <Dialog>
            <DialogTrigger>View Methodology Notes</DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Placeholder methodology</DialogTitle>
                <DialogDescription>
                  This route uses temporary preview math to exercise shared UI components.
                </DialogDescription>
              </DialogHeader>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li>Contribution timing currently toggles monthly versus yearly synthetic compounding.</li>
                <li>Scenario profile applies multipliers to expected return for visual testing.</li>
                <li>Real-dollar mode discounts final projections for inflation preview behavior.</li>
              </ul>
              <DialogFooter>
                <Button variant="outline" onClick={() => toast({ title: 'Methodology acknowledged', variant: 'success' })}>
                  Looks good
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.15fr_1fr]">
        <ChartCard
          title="Projection Shape Preview"
          description="Preview chart card with token-driven styling and responsive behavior."
        >
          <div className="rounded-xl border border-border bg-card/80 p-4">
            <div className="flex h-40 items-end justify-between gap-2">
              {sparklineBars.map((bar) => (
                <div key={bar.yearLabel} className="flex flex-1 flex-col items-center gap-2">
                  <div
                    className="w-full rounded-t-md bg-gradient-to-t from-primary/40 via-primary/65 to-teal-accent/70"
                    style={{ height: `${bar.barHeight}px` }}
                  />
                  <span className="text-[11px] text-muted-foreground">{bar.yearLabel}</span>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>

        <Card className="brand-frame">
          <CardHeader>
            <CardTitle>Scenario Milestones</CardTitle>
            <CardDescription>Reusable table primitive for yearly snapshots and withdrawal readiness.</CardDescription>
          </CardHeader>
          <CardContent>
            <Table containerClassName="max-h-[24rem]" stickyHeader compact>
              <TableHeader>
                <TableRow>
                  <TableHead numeric>Age</TableHead>
                  <TableHead numeric>Portfolio</TableHead>
                  <TableHead numeric>4% Spend</TableHead>
                  <TableHead numeric>Funded</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.year}>
                    <TableCell numeric>{row.year}</TableCell>
                    <TableCell numeric>{formatCurrency(row.portfolio)}</TableCell>
                    <TableCell numeric>{formatCurrency(row.withdrawal)}</TableCell>
                    <TableCell numeric>{Math.min(row.fundedPercent, 160).toFixed(0)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="mt-3 text-xs text-muted-foreground">
              Estimated coast age in this preview model: <strong>{projection.coastAge}</strong>
              {inflationAdjusted ? ' with inflation-adjusted contributions enabled.' : ' with flat nominal contributions.'}
            </p>
          </CardContent>
        </Card>
      </section>
    </AppLayout>
  );
}
